import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { ApiActions } from '../shared/apiActions';
import apiCall from '../shared/apiCall';

export default function ContactUsModal({ show, onHide, defaultMessage = '', source = '' }) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [message, setMessage] = useState(defaultMessage);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Update message when defaultMessage changes (e.g., when modal opens with new default)
  React.useEffect(() => {
    if (show && defaultMessage) {
      setMessage(defaultMessage);
    }
  }, [show, defaultMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError(t('contactUs.mustBeLoggedIn'));
      return;
    }

    if (!message.trim()) {
      setError(t('contactUs.pleaseEnterMessage'));
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      const response = await apiCall(
        `/api/set?action=${ApiActions.SUBMIT_CONTACT_MESSAGE}`,
        { message: message.trim(), source },
        'POST'
      );

      if (response.success) {
        setSuccess(true);
        setMessage('');
        // Keep modal open so user can see success message and close manually
      } else {
        setError(response.message || t('contactUs.failedToSubmit'));
      }
    } catch (err) {
      console.error('Error submitting contact message:', err);
      setError(t('contactUs.errorOccurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setMessage('');
      setError('');
      setSuccess(false);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>
          <i className="bi bi-envelope me-2"></i>
          {t('contactUs.title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!currentUser ? (
          <Alert variant="warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {t('contactUs.mustBeLoggedIn')}
          </Alert>
        ) : (
          <Form onSubmit={handleSubmit}>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert variant="success">
                <i className="bi bi-check-circle-fill me-2"></i>
                {t('contactUs.messageSentSuccess')}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>{t('contactUs.yourMessage')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('contactUs.enterMessage')}
                disabled={isSubmitting || success}
                maxLength={2000}
                required
              />
              <Form.Text className="text-muted">
                {message.length}/2000 {t('contactUs.characters')}
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isSubmitting || success}
              >
                {t('contactUs.cancel')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting || success || !message.trim()}
              >
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {t('contactUs.sending')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-send me-2"></i>
                    {t('contactUs.sendMessage')}
                  </>
                )}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}

